// SymbolControlStrip — lifted verbatim out of LiveTrading.tsx (God 2026-08-26).
// Same code, smaller address: a UI problem in this panel is now a 63-line file
// to read instead of a 6,900-line one. Imports are derived from what the block
// actually references, not guessed.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { API_BASE_URL } from './foundation';
import { Sheet } from '../components/ui/Sheet';
import { ExecControl } from '../components/ui/ExecControl';

export const SymbolControlStrip: React.FC = () => {
  const [kills, setKills] = useState<any[]>([]);
  const [openPos, setOpenPos] = useState<Record<string, number>>({});
  const [botSyms, setBotSyms] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/risk/symbol-controls`).then(r => r.json()).then(d => {
      if (d?.status === 'success') { setKills(d.controls || []); setOpenPos(d.openPositions || {}); }
    }).catch(() => {});
  }, []);
  // Hidden-tab guard (God 2026-09-15, iOS audit): this was the one strip poll
  // that kept hitting /deployments every 10s with the tab in the background.
  // Same guard the other pollers already use; a hidden tab pays nothing.
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => {   // every bot-basket symbol gets a chip (Sthambh cash names too)
    fetch(`${API_BASE_URL}/deployments`).then(r => r.json()).then(d => {
      const syms = ((d?.deployments || []) as any[]).flatMap(b => (b?.assets || [])
        .map((a: any) => String(a?.symbol || '').trim().toUpperCase().replace(/\s+/g, '_')).filter(Boolean));
      setBotSyms(Array.from(new Set(syms)));
    }).catch(() => {});
  }, []);
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };
  const post = async (url: string, body: any, ok: string) => {
    try {
      const r = await fetch(`${API_BASE_URL}${url}`, { method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.status === 401 || r.status === 403) { flash('⚠ admin login required'); return; }
      const d = await r.json();
      if (d?.status === 'success') { flash(ok); load(); } else flash(`✗ ${d?.message || 'failed'}`);
    } catch { flash('✗ network error'); }
  };
  // M-H2 (God D25, 2026-10-04): the chip was a ~14 px button behind window.prompt +
  // window.confirm. It is now a 48 px chip that opens a sheet; KILL / RESUME fire
  // only on a hold (ExecControl). Same endpoints, same body.
  const [sheetSym, setSheetSym] = useState<string | null>(null);
  const [reason, setReason] = useState('manual risk-off');
  const [squareOff, setSquareOff] = useState(false);
  const openSheet = (sym: string) => { setReason('manual risk-off'); setSquareOff(false); setSheetSym(sym); };
  const killSym = (sym: string) => {
    void post('/risk/symbol-kill', { symbol: sym, reason: reason.trim() || 'manual risk-off',
      squareOff: (openPos[sym] || 0) > 0 && squareOff }, `🔴 ${sym} KILLED`);
    setSheetSym(null);
  };
  const resumeSym = (sym: string) => {
    void post('/risk/symbol-kill/reset', { symbol: sym }, `🟢 ${sym} RESUMED`);
    setSheetSym(null);
  };
  const activeKills = kills.filter(k => k.killed);
  const syms = Array.from(new Set(['NIFTY_50', 'BANK_NIFTY', 'SENSEX', ...botSyms, ...Object.keys(openPos), ...kills.map(k => k.symbol)]));
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',padding:'8px 14px',background:'#090d16',borderTop:'1px solid #1a1f3a'}}>
      {/* title now lives on the accordion header (SymbolControlAccordion) */}
      {syms.map(sym => {
        const killed = activeKills.some(k => k.symbol === sym);
        return (
          <button key={sym} type="button" onClick={() => openSheet(sym)}
            title={killed ? `${sym}: tap to RESUME` : `${sym}: tap to KILL`}
            style={{display:'flex',alignItems:'center',gap:6,minHeight:48,minWidth:48,padding:'0 12px',borderRadius:8,cursor:'pointer',
            background:killed?'rgba(239,68,68,.12)':'rgba(16,185,129,.07)',
            border:`1px solid ${killed?'rgba(239,68,68,.5)':'rgba(16,185,129,.25)'}`}}>
            <span style={{fontSize:11,fontWeight:800,color:killed?'#f87171':'#34d399'}}>{killed?'🔴':'🟢'} {sym}</span>
            {(openPos[sym]||0) > 0 && <span style={{fontSize:11,color:'#fbbf24'}}>({openPos[sym]})</span>}
            <span style={{fontSize:11,fontWeight:900,color:killed?'#34d399':'#f87171'}}>{killed ? 'RESUME ›' : 'KILL ›'}</span>
          </button>);
      })}
      {msg && <span style={{fontSize:11,fontWeight:700,color:'#a5b4fc'}}>{msg}</span>}
      {sheetSym && (() => {
        const killed = activeKills.some(k => k.symbol === sheetSym);
        const open = openPos[sheetSym] || 0;
        return (
          <Sheet open onClose={() => setSheetSym(null)} title={`${sheetSym} · ${killed ? 'RESUME' : 'KILL'}`}
            actions={killed
              ? <ExecControl tone="pos" onConfirm={() => resumeSym(sheetSym)}>HOLD TO RESUME {sheetSym}</ExecControl>
              : <ExecControl tone="neg" onConfirm={() => killSym(sheetSym)}>HOLD TO KILL {sheetSym}</ExecControl>}>
            {killed ? (
              <div style={{fontSize:13,color:'#cbd5e1',lineHeight:1.5}}>New entries on {sheetSym} are blocked. Resuming lets the bots trade it again.</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <label style={{display:'flex',flexDirection:'column',gap:6,fontSize:11,fontWeight:700,color:'#94a3b8',letterSpacing:.6}}>
                  REASON
                  <input value={reason} onChange={e => setReason(e.target.value)}
                    style={{minHeight:44,padding:'0 12px',borderRadius:8,border:'1px solid #1e293b',background:'#0b1220',color:'#e2e8f0',fontSize:16}} />
                </label>
                {open > 0 && (
                  <label style={{display:'flex',alignItems:'center',gap:10,minHeight:44,fontSize:13,color:'#fbbf24',cursor:'pointer'}}>
                    <input type="checkbox" checked={squareOff} onChange={e => setSquareOff(e.target.checked)} style={{width:22,height:22}} />
                    Also square off the {open} open position(s)
                  </label>
                )}
              </div>
            )}
          </Sheet>);
      })()}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
//  SYMBOL CONTROL ACCORDION — collapsed-by-default wrapper (2026-07-22)
//  Lives INSIDE the Deployed-Strategies tab content so the L0 strip takes
//  ZERO permanent vertical space (God/Subhas: must not sit exposed above the
//  ALL/MAMTA/MANUAL/TV/POSITIONS filter tabs). Click to expand the kill chips.
// ══════════════════════════════════════════════════════════════
